import { useEffect, useRef, useState } from 'react'

/**
 * One restrained motion system for the marketing pages — a single orchestrated
 * entrance, not scattered effects.
 *
 * useReveal(): call once per page. Client-side only, it tags section headings
 * and cards with a pending state and flips them (staggered for card siblings)
 * as they enter the viewport. Because the classes are added by JS, bots and
 * no-JS visitors always see fully-visible content — nothing is hidden in the
 * markup. prefers-reduced-motion disables the whole thing.
 */
export function useReveal(rootSelector: string): void {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const root = document.querySelector(rootSelector)
    if (!root) return

    const style = document.createElement('style')
    style.textContent = `
      .reveal-pending { opacity: 0; transform: translateY(12px); }
      .reveal-in { opacity: 1; transform: none; transition: opacity .55s ease, transform .55s cubic-bezier(.22,.9,.32,1); }
    `
    document.head.append(style)

    const targets = root.querySelectorAll('section h2, .cp-card')
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const el = entry.target as HTMLElement
          // Stagger cards against their siblings; headings rise alone.
          const siblings = el.classList.contains('cp-card')
            ? [...(el.parentElement?.children ?? [])].filter((c) => c.classList.contains('cp-card'))
            : []
          const idx = Math.max(0, siblings.indexOf(el))
          el.style.transitionDelay = `${(idx % 8) * 45}ms`
          el.classList.add('reveal-in')
          io.unobserve(el)
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    )
    for (const el of targets) {
      // Skip anything already on screen at boot — the hero must never blink.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) continue
      el.classList.add('reveal-pending')
      io.observe(el)
    }
    // Safety net: if the observer never fires (hidden tab, exotic browser),
    // nothing may stay invisible — reveal all stragglers after a beat.
    const failsafe = window.setTimeout(() => {
      for (const el of document.querySelectorAll('.reveal-pending:not(.reveal-in)')) {
        el.classList.add('reveal-in')
      }
    }, 6000)
    return () => {
      io.disconnect()
      clearTimeout(failsafe)
      style.remove()
      for (const el of targets) el.classList.remove('reveal-pending', 'reveal-in')
    }
  }, [rootSelector])
}

/**
 * Count a stat up once when it scrolls into view. Accepts the display strings
 * we actually use ("234", "72,000+", "10/10", "2 to 10 min"): the leading number
 * animates, the suffix stays put. Values under 10 (and reduced-motion) render
 * static — counting to 3 reads as a glitch, not a flourish.
 */
export function CountUp({ value }: { value: string }) {
  const m = value.match(/^([\d,]+)(.*)$/)
  const target = m?.[1] ? Number(m[1].replace(/,/g, '')) : Number.NaN
  const suffix = m?.[2] ?? ''
  const animatable =
    Number.isFinite(target) &&
    target >= 10 &&
    typeof window !== 'undefined' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const ref = useRef<HTMLSpanElement>(null)
  const [display, setDisplay] = useState(animatable ? '0' : value)

  useEffect(() => {
    if (!animatable || !ref.current) return
    let raf = 0
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        io.disconnect()
        const t0 = performance.now()
        const dur = 900
        const tick = (t: number) => {
          const p = Math.min(1, (t - t0) / dur)
          const eased = 1 - (1 - p) ** 3
          setDisplay(Math.round(target * eased).toLocaleString('en-US'))
          if (p < 1) raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
      },
      { threshold: 0.4 }
    )
    io.observe(ref.current)
    // Same failsafe as useReveal: never leave a stat stuck at 0.
    const failsafe = window.setTimeout(() => setDisplay(target.toLocaleString('en-US')), 7000)
    return () => {
      io.disconnect()
      clearTimeout(failsafe)
      cancelAnimationFrame(raf)
    }
  }, [animatable, target])

  return (
    <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {animatable ? `${display}${suffix}` : value}
    </span>
  )
}
